import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_CENTER = { lat: 22.9734, lng: 78.6569 };
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 13;
const SEARCH_DEBOUNCE_MS = 250;

const BRAND = {
  red: "#D73C2E",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  text: "#252935",
  muted: "#6b6f7d",
  backdrop: "rgba(37,41,53,0.36)",
};

let googleMapsLoaderPromise = null;

function parseCoordinate(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCoordinate(value) {
  if (!Number.isFinite(Number(value))) return "";
  return Number(value).toFixed(6);
}

function loadGoogleMapsApi(apiKey) {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = `__jkGoogleMapsInit_${Date.now()}`;
    const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google.maps);
    };

    if (existingScript) {
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&callback=${callbackName}&v=weekly`;
    script.onerror = () => {
      delete window[callbackName];
      googleMapsLoaderPromise = null;
      reject(new Error("Failed to load Google Maps."));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

function createDraft({ latitude, longitude, address }) {
  return {
    latitude: String(latitude || ""),
    longitude: String(longitude || ""),
    address: String(address || ""),
  };
}

export default function BirthLocationMapField({
  latitude,
  longitude,
  address,
  onLatitudeChange,
  onLongitudeChange,
  onAddressChange,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const geocodeRequestIdRef = useRef(0);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [draft, setDraft] = useState(() => createDraft({ latitude, longitude, address }));
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapState, setMapState] = useState({
    loading: false,
    ready: false,
    error: "",
  });
  const [searchState, setSearchState] = useState({
    loading: false,
    error: "",
  });

  const displayAddress = String(address || "").trim();
  const hasSavedLocation =
    Boolean(displayAddress) ||
    (parseCoordinate(latitude) !== null && parseCoordinate(longitude) !== null);
  const draftLatitudeNumber = useMemo(() => parseCoordinate(draft.latitude), [draft.latitude]);
  const draftLongitudeNumber = useMemo(() => parseCoordinate(draft.longitude), [draft.longitude]);
  const hasDraftCoordinates = draftLatitudeNumber !== null && draftLongitudeNumber !== null;

  useEffect(() => {
    let cancelled = false;

    if (!GOOGLE_MAPS_API_KEY) {
      setMapState({ loading: false, ready: false, error: "" });
      return undefined;
    }

    setMapState((prev) => ({ ...prev, loading: true, error: "" }));

    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (cancelled) return;
        geocoderRef.current = geocoderRef.current || new window.google.maps.Geocoder();
        autocompleteServiceRef.current =
          autocompleteServiceRef.current || new window.google.maps.places.AutocompleteService();
        setMapState({ loading: false, ready: true, error: "" });
      })
      .catch((error) => {
        if (cancelled) return;
        setMapState({
          loading: false,
          ready: false,
          error: error?.message || "Google Maps could not be loaded.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!overlayOpen) {
      clickListenerRef.current?.remove?.();
      clickListenerRef.current = null;
      markerRef.current?.setMap?.(null);
      markerRef.current = null;
      mapRef.current = null;
      return;
    }

    if (!mapState.ready || !mapNodeRef.current || mapRef.current || !window.google?.maps) return;

    mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
      center: hasDraftCoordinates ? { lat: draftLatitudeNumber, lng: draftLongitudeNumber } : DEFAULT_CENTER,
      zoom: hasDraftCoordinates ? SELECTED_ZOOM : DEFAULT_ZOOM,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });

    clickListenerRef.current = mapRef.current.addListener("click", (event) => {
      const nextLat = formatCoordinate(event.latLng.lat());
      const nextLng = formatCoordinate(event.latLng.lng());
      setDraft((current) => ({
        ...current,
        latitude: nextLat,
        longitude: nextLng,
      }));
    });
  }, [draftLatitudeNumber, draftLongitudeNumber, hasDraftCoordinates, mapState.ready, overlayOpen]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    if (!hasDraftCoordinates) {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(DEFAULT_ZOOM);
      return;
    }

    const position = { lat: draftLatitudeNumber, lng: draftLongitudeNumber };

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position,
        map: mapRef.current,
        draggable: true,
      });

      markerRef.current.addListener("dragend", (event) => {
        const nextLat = formatCoordinate(event.latLng.lat());
        const nextLng = formatCoordinate(event.latLng.lng());
        setDraft((current) => ({
          ...current,
          latitude: nextLat,
          longitude: nextLng,
        }));
      });
    } else {
      markerRef.current.setPosition(position);
      markerRef.current.setMap(mapRef.current);
    }

    mapRef.current.setCenter(position);
    mapRef.current.setZoom(SELECTED_ZOOM);
  }, [draftLatitudeNumber, draftLongitudeNumber, hasDraftCoordinates]);

  const resolveAddressFromCoordinates = useCallback((nextLat, nextLng) => {
    if (!geocoderRef.current) return;
    const requestId = geocodeRequestIdRef.current + 1;
    geocodeRequestIdRef.current = requestId;

    geocoderRef.current.geocode(
      { location: { lat: nextLat, lng: nextLng } },
      (results, status) => {
        if (requestId !== geocodeRequestIdRef.current) return;
        if (status !== "OK" || !Array.isArray(results) || !results.length) return;
        const nextAddress = results[0].formatted_address || "";
        setDraft((current) => (current.address === nextAddress ? current : { ...current, address: nextAddress }));
        setSearchQuery(nextAddress);
      }
    );
  }, []);

  useEffect(() => {
    if (!overlayOpen || !hasDraftCoordinates || !mapState.ready) return;
    resolveAddressFromCoordinates(draftLatitudeNumber, draftLongitudeNumber);
  }, [draftLatitudeNumber, draftLongitudeNumber, hasDraftCoordinates, mapState.ready, overlayOpen, resolveAddressFromCoordinates]);

  useEffect(() => {
    if (!overlayOpen || !mapState.ready || !autocompleteServiceRef.current) return undefined;

    const query = String(searchQuery || "").trim();
    if (query.length < 2) {
      setSuggestions([]);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: query },
        (predictions = [], status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions.length) {
            setSuggestions([]);
            return;
          }

          setSuggestions(
            predictions.map((prediction) => ({
              placeId: prediction.place_id,
              description: prediction.description,
              primaryText: prediction.structured_formatting?.main_text || prediction.description,
              secondaryText: prediction.structured_formatting?.secondary_text || "",
            }))
          );
          setShowSuggestions(true);
        }
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [mapState.ready, overlayOpen, searchQuery]);

  const updateDraftFromGeocode = useCallback((request) => {
    if (!geocoderRef.current) return;

    setSearchState({ loading: true, error: "" });

    geocoderRef.current.geocode(request, (results, status) => {
      if (status !== "OK" || !Array.isArray(results) || !results.length) {
        setSearchState({
          loading: false,
          error: "No matching place found. Try a more specific location.",
        });
        return;
      }

      const location = results[0].geometry?.location;
      if (!location) {
        setSearchState({
          loading: false,
          error: "Location result did not include coordinates.",
        });
        return;
      }

      setDraft({
        latitude: formatCoordinate(location.lat()),
        longitude: formatCoordinate(location.lng()),
        address: results[0].formatted_address || "",
      });
      setSearchQuery(results[0].formatted_address || "");
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchState({ loading: false, error: "" });
    });
  }, []);

  const openOverlay = () => {
    const nextDraft = createDraft({ latitude, longitude, address });
    setDraft(nextDraft);
    setSearchQuery(nextDraft.address);
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchState({ loading: false, error: "" });
    setOverlayOpen(true);
  };

  const closeOverlay = () => {
    setOverlayOpen(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchState({ loading: false, error: "" });
  };

  const handleSearch = () => {
    const query = String(searchQuery || "").trim();
    if (!query || !geocoderRef.current) return;
    updateDraftFromGeocode({ address: query });
  };

  const handleSuggestionPick = (suggestion) => {
    setSearchQuery(suggestion.description);
    updateDraftFromGeocode({ placeId: suggestion.placeId });
  };

  const handleSave = () => {
    onLatitudeChange(draft.latitude || "");
    onLongitudeChange(draft.longitude || "");
    onAddressChange?.(draft.address || "");
    closeOverlay();
  };

  const handleClearSaved = () => {
    onLatitudeChange("");
    onLongitudeChange("");
    onAddressChange?.("");
  };

  const handleClearDraft = () => {
    setDraft({ latitude: "", longitude: "", address: "" });
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <>
      <div style={S.inlineWrap}>
        <div style={S.addressText}>{displayAddress || "No location added"}</div>
        <div style={S.actionRow}>
          <button type="button" style={S.primaryBtn} onClick={openOverlay}>
            {hasSavedLocation ? "Change Location (Optional)" : "Add Location (Optional)"}
          </button>
          {hasSavedLocation ? (
            <button type="button" style={S.secondaryBtn} onClick={handleClearSaved}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {overlayOpen ? (
        <div style={S.backdrop} onClick={closeOverlay}>
          <div style={S.modal} onClick={(event) => event.stopPropagation()}>
            <div style={S.searchRow}>
              <input
                style={S.input}
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => {
                  if (suggestions.length) setShowSuggestions(true);
                }}
                onBlur={() => {
                  window.setTimeout(() => setShowSuggestions(false), 150);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Search place on map, e.g. Baramati, Pune"
              />
              <button type="button" style={S.searchBtn} onClick={handleSearch} disabled={!mapState.ready || searchState.loading}>
                {searchState.loading ? "Searching..." : "Search"}
              </button>
            </div>

            {showSuggestions && suggestions.length > 0 ? (
              <div style={S.suggestionList}>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.placeId}
                    type="button"
                    style={S.suggestionItem}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSuggestionPick(suggestion)}
                  >
                    <span style={S.suggestionPrimary}>{suggestion.primaryText}</span>
                    {suggestion.secondaryText ? <span style={S.suggestionSecondary}>{suggestion.secondaryText}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}

            {searchState.error ? <div style={S.searchError}>{searchState.error}</div> : null}

            {GOOGLE_MAPS_API_KEY ? (
              <div style={S.mapShell}>
                <div ref={mapNodeRef} style={S.mapCanvas} />
                {mapState.loading ? <div style={S.overlay}>Loading Google Map...</div> : null}
                {mapState.error ? <div style={S.overlay}>{mapState.error}</div> : null}
              </div>
            ) : (
              <div style={S.notice}>
                Add `REACT_APP_GOOGLE_MAPS_API_KEY` to `.env.local` to enable map-based coordinate selection.
              </div>
            )}

            <div style={S.modalActions}>
              <button type="button" style={S.secondaryBtn} onClick={closeOverlay}>
                Cancel
              </button>
              <button type="button" style={S.secondaryBtn} onClick={handleClearDraft}>
                Clear
              </button>
              <button type="button" style={S.primaryBtn} onClick={handleSave}>
                Save Location
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const S = {
  inlineWrap: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  addressText: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.5,
    wordBreak: "break-word",
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 12,
    padding: "10px 12px",
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    background: "#fff",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: BRAND.backdrop,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  modal: {
    width: "min(960px, 100%)",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    background: "#fff",
    borderRadius: 20,
    padding: 20,
    display: "grid",
    gap: 14,
    boxShadow: "0 24px 60px rgba(37,41,53,0.22)",
  },
  searchRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  input: {
    width: "100%",
    borderRadius: 12,
    border: `1px solid ${BRAND.borderStrong}`,
    padding: "10px 12px",
    outline: "none",
    fontSize: 14,
    background: "#fff",
  },
  searchBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.red,
    color: "#fff",
    borderRadius: 12,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  suggestionList: {
    marginTop: -6,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 18px 48px rgba(47,17,19,0.08)",
  },
  suggestionItem: {
    width: "100%",
    border: "none",
    borderBottom: `1px solid ${BRAND.border}`,
    background: "#fff",
    padding: "12px 14px",
    textAlign: "left",
    display: "grid",
    gap: 4,
    cursor: "pointer",
  },
  suggestionPrimary: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 800,
  },
  suggestionSecondary: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 600,
  },
  searchError: {
    color: BRAND.red,
    fontSize: 12,
    fontWeight: 700,
  },
  mapShell: {
    position: "relative",
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${BRAND.border}`,
    minHeight: 420,
    background: "#f7f1ec",
  },
  mapCanvas: {
    width: "100%",
    height: 420,
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.78)",
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 800,
    padding: 16,
    textAlign: "center",
  },
  notice: {
    borderRadius: 16,
    border: `1px dashed ${BRAND.borderStrong}`,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
    padding: "12px 14px",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.red,
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  secondaryBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.red,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};
